import React, { useState } from "react";
import { CHART_1, CHART_2, CHART_3, INK, MUTED, GRID, PAPER } from "./palette";

// ---- r2_blocks: hierarchical regression's R², block by block, out of 100% ----

// One stacked bar of the outcome's whole variation: block 1's R² (green, the controls), the slice
// each later block adds, its ΔR² (teal, the idea; a middle block takes chart-3), and what the final
// model leaves unexplained (gray). Props come from ChartData#r2_blocks:
//   blocks [{ names, r2, change, f?, df1?, df2?, p? }] (r2 is the model's R² once that block is in),
//   unexplained (1 − the last R²), outcomeName ("burnout").
// The tokens and the Legend/Tooltip conventions match ConceptChart.jsx.
const W = 640;
const M = { top: 16, right: 16, left: 16 };

const fmt = (x, places = 2) => (x < 0 ? "−" : "") + Math.abs(x).toFixed(places);
// APA style for values that can't exceed 1: ".121".
const fmtR = (x, places = 3) => fmt(x, places).replace(/^(−?)0\./, "$1.");
const pct = (x) => `${(x * 100).toFixed(1)}%`;
const fmtP = (p) => (p < 0.001 ? "p < .001" : `p = ${fmtR(p, 3)}`);
const sentenceList = (parts) => (parts.length < 2 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`);
// Lowercase a column name for the middle of a sentence, but keep acronyms like GAD-7.
const lower = (s) => (/^.[A-Z0-9]/.test(s) ? s : s.toLowerCase());
const upperFirst = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const Halo = ({ children, ...props }) => <text fontSize="11" stroke={PAPER} strokeWidth="3" paintOrder="stroke" strokeLinejoin="round" {...props}>{children}</text>;

function Legend({ items }) {
	return (
		<div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
			{items.map(({ label, color }) => (
				<span key={label} className="flex items-center gap-1.5">
					<span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
					{label}
				</span>
			))}
		</div>
	);
}

function Tooltip({ x, lines }) {
	const longest = Math.max(...lines.map((l) => String(l).length));
	const width = Math.max(170, Math.round(longest * 6.4) + 16), left = Math.min(Math.max(x + 8, M.left), W - M.right - width);
	return (
		<g pointerEvents="none" transform={`translate(${left},${M.top})`}>
			<rect width={width} height={10 + lines.length * 15} rx="3" fill={PAPER} stroke={GRID} />
			{lines.map((line, i) => (
				<text key={i} x="8" y={17 + i * 15} fontSize="11" fill={i === 0 ? MUTED : INK} fontWeight={i === 0 ? 400 : 600}>{line}</text>
			))}
		</g>
	);
}

export default function R2BlocksChart({ blocks = [], unexplained, outcomeName = "the outcome" }) {
	const [hover, setHover] = useState(null);
	const id = React.useId().replace(/:/g, "");
	if (blocks.length < 2) return null;
	const later = blocks.length - 1;
	// Later blocks' labels sit in rows above the bar, the last block's nearest it.
	const rowH = 18, barT = M.top + 22 + later * rowH, barH = 40, barB = barT + barH;
	const height = barB + 62;
	const l = M.left, r = W - M.right;
	const x = (v) => l + v * (r - l);
	const last = blocks[blocks.length - 1];
	const colorOf = (i) => (i === 0 ? CHART_2 : i === later ? CHART_1 : CHART_3);
	const names = (b) => sentenceList(b.names.map(lower));
	const fLine = (b, change) => (b.f != null && b.df1 != null && b.df2 != null
		? `${change ? "F change" : "F"}(${b.df1}, ${b.df2}) = ${fmt(b.f)}${b.p != null ? `, ${fmtP(b.p)}` : ""}` : null);
	const segments = [
		...blocks.map((b, i) => {
			const start = i === 0 ? 0 : blocks[i - 1].r2;
			return {
				key: `b${i}`, start, size: b.change, color: colorOf(i),
				lines: i === 0
					? [`Block 1: ${names(b)}`, `R² = ${fmtR(b.r2)}: ${pct(b.r2)} of the variation in ${outcomeName}`, fLine(b, false)]
					: [`Block ${i + 1} adds ${names(b)}`, `ΔR² = ${fmtR(b.change)}: another ${pct(b.change)}`, `R² goes from ${fmtR(start)} to ${fmtR(b.r2)}`, fLine(b, true)]
			};
		}),
		{ key: "rest", start: last.r2, size: unexplained, color: GRID,
			lines: ["Left unexplained", `1 − ${fmtR(last.r2)} = ${fmtR(unexplained)}: ${pct(unexplained)}`, `Variation in ${outcomeName} the final model doesn't predict`] }
	].map((s) => ({ ...s, lines: s.lines.filter(Boolean) }));
	const dim = (key) => (hover === null || hover.key === key ? 1 : 0.6);
	const legend = [
		{ label: `Block 1's R²: ${names(blocks[0])}`, color: CHART_2 },
		...blocks.slice(1).map((b, i) => ({ label: `ΔR², added by block ${i + 2}: ${names(b)}`, color: colorOf(i + 1) })),
		{ label: "Left unexplained", color: GRID }
	];
	return (
		<figure>
			<Legend items={legend} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`The variation in ${outcomeName}, 100% in all. Block 1, ${names(blocks[0])}: R² = ${fmtR(blocks[0].r2)}, ${pct(blocks[0].r2)}.${blocks.slice(1).map((b, i) => ` Block ${i + 2} adds ${names(b)}: ΔR² = ${fmtR(b.change)}, another ${pct(b.change)}, for R² = ${fmtR(b.r2)}.`).join("")} Left unexplained: ${pct(unexplained)}.`}>
				<defs>
					<clipPath id={`${id}-bar`}><rect x={l} y={barT} width={r - l} height={barH} rx="4" /></clipPath>
				</defs>
				<g clipPath={`url(#${id}-bar)`}>
					{segments.map((s) => (
						<rect key={s.key} x={x(s.start)} y={barT} width={Math.max(0, x(s.start + s.size) - x(s.start))} height={barH}
							fill={s.color} opacity={dim(s.key)} stroke={PAPER} strokeWidth="2" />
					))}
				</g>
				{/* Each later block's slice, labelled above the bar with a leader line. */}
				{blocks.slice(1).map((b, i) => {
					const cx = (x(blocks[i].r2) + x(b.r2)) / 2, ty = barT - 14 - (later - 1 - i) * rowH;
					const anchor = cx > W * 0.6 ? "end" : "start";
					return (
						<g key={`label${i}`}>
							<line x1={cx} x2={cx} y1={ty + 3} y2={barT - 1} stroke={MUTED} />
							<Halo x={anchor === "start" ? cx - 4 : cx + 4} y={ty} textAnchor={anchor} fill={INK} fontWeight="600">
								Block {i + 2} adds {names(b)}: ΔR² = {fmtR(b.change)} ({pct(b.change)})
							</Halo>
						</g>
					);
				})}
				<text x={l} y={barB + 16} fontSize="11" fill={INK} fontWeight="600">Block 1, {names(blocks[0])}: R² = {fmtR(blocks[0].r2)} ({pct(blocks[0].r2)})</text>
				{/* The final model's R², bracketed under everything the blocks predict together. */}
				<path d={`M${l},${barB + 26} V${barB + 31} H${x(last.r2)} V${barB + 26}`} fill="none" stroke={MUTED} />
				<text x={l} y={barB + 46} fontSize="11" fill={MUTED}>Model {blocks.length}, {blocks.length === 2 ? "both blocks" : `all ${blocks.length} blocks`}: R² = {fmtR(last.r2)} ({pct(last.r2)})</text>
				<text x={(x(last.r2) + r) / 2} y={barT + barH / 2} fontSize="12" fill={INK} textAnchor="middle" dominantBaseline="middle" pointerEvents="none">
					Left unexplained: {pct(unexplained)}
				</text>
				{/* Hit targets, the smallest last so a thin slice's widened target stays on top. */}
				{[...segments].sort((a, b) => b.size - a.size).map((s) => (
					<rect key={`hit${s.key}`} x={x(s.start) - (s.size < 0.03 ? 6 : 0)} y={barT - 4} width={x(s.start + s.size) - x(s.start) + (s.size < 0.03 ? 12 : 0)} height={barH + 8}
						fill="transparent" onMouseEnter={() => setHover({ key: s.key, cx: x(s.start), lines: s.lines })} onMouseLeave={() => setHover(null)} />
				))}
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				The whole bar is the variation in {outcomeName}. {upperFirst(names(blocks[0]))} {blocks[0].names.length > 1 ? "predict" : "predicts"} {pct(blocks[0].r2)} of it; {blocks.slice(1).map((b) => `adding ${names(b)} predicts another ${pct(b.change)}`).join(", then ")}, for {pct(last.r2)} in all.
				{" "}ΔR² is {later > 1 ? "each later block's slice" : "the teal slice"}: what a block predicts beyond the blocks before it, so it depends on which blocks went in first. “Predict” is statistical here, not a claim about cause.
			</figcaption>
		</figure>
	);
}
